import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setCompany } from '@/store/companySlice';
import { useFilteredCompanies } from '@/hooks/useFilteredCompanies';
import { setSelectedCategories, setSelectedEmployees, setSelectedProductCount, setSelectedModalities } from '@/store/companiesFilterSlice';
import { useCompanyData } from '@/hooks/useCompanyData';

// GraphQL query moved to shared useCompanyData hook

export const useCompanyDetailsLogic = () => {
  const params = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getAdjacentCompanySlugs } = useFilteredCompanies();

  const slug = params.slug || '';
  const searchParams = new URLSearchParams(location.search);
  const pageSubslug = searchParams.get('page') || '';

  const [activeTab, setActiveTab] = useState('plans');
  const [activePage, setActivePage] = useState(pageSubslug);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isTabContentVisible, setIsTabContentVisible] = useState(true);

  // Use the shared company data hook
  const { data, isLoading, error, refetch } = useCompanyData({
    slug,
    enabled: !!slug
  });

  useEffect(() => {
    if (data) {
      // Map API Company type to Redux Company type
      const mappedCompany = {
        ...data,
        logoUrl: data.logo_url || null,
        employees: data.employees?.toString() || '',
        category: data.category || '',
        description: data.description || null,
        url: data.url || null,
        legacySlug: null,
        pricingMetrics: data.pages?.flatMap(p => p.pricing_metrics || []).map(metric => ({
          id: metric?.id || '',
          slug: metric?.display_name?.toLowerCase().replace(/ /g, '-') || '',
          display_name: metric?.display_name || '',
          modality: metric?.modality || null,
          subcategory: null,
          sample_values: metric?.sample_values || null,
          sample_prices: null,
          created_at: null
        })) || [],
        pages: data.pages?.map(page => ({
          id: page.id,
          subslug: page.subslug,
          pricingPageUrl: page.pricing_page_url || null,
          screenshotUrl: page.scrapes?.[0]?.screenshot_url || null,
          screenshotUrlVersion: page.scrapes?.[0]?.version || null,
          pricingMetrics: page.pricing_metrics || [],
          tags: page.tags || [],
          scrapes: page.scrapes || []
        })) || []
      };
      
      dispatch(setCompany(mappedCompany));
    }
  }, [data, dispatch]);

  const categoriesParam = searchParams.get('categories');
  const employeesParam = searchParams.get('employees');
  const productCountParam = searchParams.get('productCount');
  const modalitiesParam = searchParams.get('modalities');

  const selectedCategories = useAppSelector(state => state.companiesFilter.selectedCategories) || [];
  const selectedEmployees = useAppSelector(state => state.companiesFilter.selectedEmployees) || [];
  const selectedProductCount = useAppSelector(state => state.companiesFilter.selectedProductCount) || [];
  const selectedModalities = useAppSelector(state => state.companiesFilter.selectedModalities) || [];

  useEffect(() => {
    if (categoriesParam) {
      const categories = categoriesParam.split(',');
      if (JSON.stringify(categories) !== JSON.stringify(selectedCategories)) {
        dispatch(setSelectedCategories(categories));
      }
    }
    if (employeesParam) {
      const employees = employeesParam.split(',');
      if (JSON.stringify(employees) !== JSON.stringify(selectedEmployees)) {
        dispatch(setSelectedEmployees(employees));
      }
    }
    if (productCountParam) {
      const productCount = productCountParam.split(',');
      if (JSON.stringify(productCount) !== JSON.stringify(selectedProductCount)) {
        dispatch(setSelectedProductCount(productCount));
      }
    }
    if (modalitiesParam) {
      const modalities = modalitiesParam.split(',');
      if (JSON.stringify(modalities) !== JSON.stringify(selectedModalities)) {
        dispatch(setSelectedModalities(modalities));
      }
    }
  }, [categoriesParam, employeesParam, productCountParam, modalitiesParam, dispatch, selectedCategories, selectedEmployees, selectedProductCount, selectedModalities]);

  const navigateToPreviousCompany = () => {
    const { prevSlug } = getAdjacentCompanySlugs(slug);
    if (!prevSlug) return;
    const queryParams = new URLSearchParams();
    if (activePage) queryParams.set('page', activePage);
    navigate(`/companies/${prevSlug}${queryParams.toString() ? `?${queryParams}` : ''}`);
  };

  const navigateToNextCompany = () => {
    const { nextSlug } = getAdjacentCompanySlugs(slug);
    if (!nextSlug) return;
    const queryParams = new URLSearchParams();
    if (activePage) queryParams.set('page', activePage);
    navigate(`/companies/${nextSlug}${queryParams.toString() ? `?${queryParams}` : ''}`);
  };

  const handlePageChange = (page: string) => {
    setIsTabContentVisible(false);
    setTimeout(() => {
      setActivePage(page);
      setTimeout(() => setIsTabContentVisible(true), 50);
    }, 200);
  };

  const handleTabChange = (tab: string) => {
    setIsTabContentVisible(false);
    setTimeout(() => {
      setActiveTab(tab);
      setTimeout(() => setIsTabContentVisible(true), 50);
    }, 200);
  };

  return {
    slug,
    data,
    isLoading,
    error,
    refetch,
    activeTab,
    activePage,
    showEditDialog,
    setShowEditDialog,
    handlePageChange,
    handleTabChange,
    isTabContentVisible,
    navigateToNextCompany,
    navigateToPreviousCompany
  };
};

export default useCompanyDetailsLogic;
